import { Link } from "react-router-dom";
import { ShieldCheck, Truck, MessageSquare, BadgeCheck, RotateCcw, Ban, Clock } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

const STEPS = [
  { icon: ShieldCheck, k: "bp_step1_t", t: "You pay — your money is protected", k2: "bp_step1_d", d: "Every payment is held under VISMARKS Buyer Protection until you get what you ordered." },
  { icon: Truck, k: "bp_step2_t", t: "Seller dispatches within 48 hours", k2: "bp_step2_d", d: "Sellers must ship with tracking info within 48 hours — or the order is flagged, the seller is struck, and your refund is issued automatically." },
  { icon: BadgeCheck, k: "bp_step3_t", t: "You confirm delivery", k2: "bp_step3_d", d: "Tap “Confirm delivery” when your order arrives. If you don't, the order auto-completes after 10 days of dispatch." },
  { icon: MessageSquare, k: "bp_step4_t", t: "Problem? Open a dispute", k2: "bp_step4_d", d: "Report a problem right from your orders page. Chat directly with the seller, and our team steps in if it isn't resolved." },
];

const GUARANTEES = [
  { icon: BadgeCheck, k: "bp_g1", t: "Verified sellers — every seller passes ID verification (KYC) before they can receive payouts." },
  { icon: Clock, k: "bp_g2", t: "48-hour dispatch rule — no dispatch means an automatic strike and an automatic refund to you." },
  { icon: RotateCcw, k: "bp_g3", t: "Automatic refunds — overdue orders and disputes unresolved after 7 days are refunded to your original payment method, no forms to fill." },
  { icon: MessageSquare, k: "bp_g4", t: "Dispute chat — talk to the seller inside the dispute; VISMARKS support can join and decide." },
  { icon: Ban, k: "bp_g5", t: "Three strikes and out — sellers with 3 strikes are banned and all their listings are paused." },
];

export default function BuyerProtection() {
  const { t } = useI18n();
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14" data-testid="buyer-protection-page">
      <div className="text-xs uppercase tracking-widest text-emerald-400 font-bold flex items-center gap-2">
        <ShieldCheck className="h-4 w-4" /> {t("bp_kicker", "VISMARKS Buyer Protection")}
      </div>
      <h1 className="mt-3 font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="bp-title">
        {t("bp_title", "Your money is protected until delivery.")}
      </h1>
      <p className="mt-4 text-neutral-400 max-w-2xl text-base md:text-lg">
        {t("bp_intro", "Shop with confidence on VISMARKS. Every seller is identity-verified, every order is tracked, and if anything goes wrong you get your money back — automatically.")}
      </p>

      <div className="mt-12 grid sm:grid-cols-2 gap-4" data-testid="bp-steps">
        {STEPS.map((s, i) => (
          <div key={s.k} className="rounded-2xl border border-[#27272A] bg-[#121212] p-6">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
                <s.icon className="h-4.5 w-4.5" size={18} />
              </span>
              <span className="text-[10px] font-mono text-neutral-500">{t("bp_step", "STEP")} {i + 1}</span>
            </div>
            <div className="mt-3 font-bold text-white">{t(s.k, s.t)}</div>
            <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{t(s.k2, s.d)}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-14 font-black tracking-tight text-lg uppercase text-neutral-300">
        {t("bp_guarantees_h", "The VISMARKS guarantee")}
      </h2>
      <ul className="mt-5 space-y-3" data-testid="bp-guarantees">
        {GUARANTEES.map((g) => (
          <li key={g.k} className="flex items-start gap-3 rounded-xl border border-[#27272A] bg-black/30 p-4">
            <g.icon className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
            <span className="text-sm text-neutral-300">{t(g.k, g.t)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-14 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8">
        <h2 className="font-black text-lg text-white">{t("bp_refund_h", "How refunds work")}</h2>
        <p className="mt-2 text-sm text-neutral-300 leading-relaxed max-w-3xl">
          {t("bp_refund_d", "If a seller fails to dispatch within 48 hours, or a dispute is decided in your favour (or stays unresolved for 7 days), VISMARKS automatically refunds the full amount to the card or account you paid with — Paystack or Stripe — usually within 5–10 business days depending on your bank.")}
        </p>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/discover" data-testid="bp-shop-btn"
          className="px-6 h-12 leading-[48px] rounded-full bg-white text-black font-bold text-sm hover:bg-neutral-200 transition-colors">
          {t("bp_cta_shop", "Shop protected drops")}
        </Link>
        <Link to="/orders" data-testid="bp-orders-btn"
          className="px-6 h-12 leading-[48px] rounded-full border border-[#27272A] text-white font-bold text-sm hover:border-neutral-500 transition-colors">
          {t("bp_cta_orders", "View my orders")}
        </Link>
      </div>
    </div>
  );
}
